import React, { type ComponentProps, forwardRef } from 'react';
import type { TextInstance } from 'react-native';
import { StyleSheet } from 'react-native';

import { TYPE_CONFIG, WEIGHT_MAP } from '@garnish/constants';

import { useResponsive } from '../../../hooks';
import { Text } from '../Text';

// ─────────────────────────────────────────────────────────────────────────────

export const BodyText = forwardRef<TextInstance, BodyTextProps>((props, ref) => {
  const { children, sizeMatch = BODY_TEXT_DEFAULT_SIZE_MATCH, bold, style, ...restProps } = props;

  const { match } = useResponsive();

  const fontSize = match(sizeMatch);
  const textStyles = BODY_TEXT_STYLES[fontSize] ?? BODY_TEXT_STYLES[BODY_TEXT_DEFAULT_FONT_SIZE];
  const boldTextStyles = bold ? styles.boldText : undefined;
  const allStyles = [textStyles, boldTextStyles, style];

  return (
    <Text ref={ref} style={allStyles} bold={bold} {...restProps}>
      {children}
    </Text>
  );
});

// ─── Constants ───────────────────────────────────────────────────────────────

const BODY_TEXT_STYLES = TYPE_CONFIG.BODY;

const BODY_TEXT_DEFAULT_SIZE_MATCH: BodyTextFontSize[] = ['14', '16'];
const BODY_TEXT_DEFAULT_FONT_SIZE: BodyTextFontSize = '16';

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  boldText: {
    fontWeight: WEIGHT_MAP.bold,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type BodyTextProps = Readonly<{
  sizeMatch?: BodyTextFontSize[];
}> &
  ComponentProps<typeof Text>;

type BodyTextFontSize = keyof typeof BODY_TEXT_STYLES;
